
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo, useEffect } from "react";
import { X, Copy, CheckCircle, QrCode, Share2, Info, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeCanvas } from 'qrcode.react';
import { Token } from '../../../types';
import { ChainIcon, TokenIcon } from "../../ui/ChainIcons";
import { Button } from "../../ui/Button";

interface DepositModalProps {
    onClose: () => void;
    address?: string;
    balances: Token[];
}

export const DepositModal: React.FC<DepositModalProps> = ({ onClose, address, balances }) => {
    const [selectedChain, setSelectedChain] = useState<'SOL' | 'SUI'>(balances[0]?.chain || 'SUI');
    const filteredTokens = useMemo(() => balances.filter(t => t.chain === selectedChain), [balances, selectedChain]);
    const [selectedToken, setSelectedToken] = useState<Token | null>(filteredTokens[0] || null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        const firstToken = balances.find(t => t.chain === selectedChain);
        setSelectedToken(firstToken || null);
    }, [selectedChain, balances]);

    const handleCopy = (text: string) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }

    const handleShare = async () => {
        if (navigator.share && address) {
            try {
                await navigator.share({
                    title: 'GhostPay Deposit Address',
                    text: `Send ${selectedToken?.symbol} on ${selectedChain} to this address:`,
                    url: address,
                });
            } catch (err) {
                console.error('Share failed:', err);
            }
        }
    };
    
    return (
    <motion.div 
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
        onClick={onClose}
    >
      <motion.div 
        initial={{ scale: 0.9, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 10 }}
        className="bg-white dark:bg-[#15171C] rounded-card shadow-2xl w-full max-w-md relative border border-stone-200 dark:border-[#2D3142] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-stone-100 dark:border-white/5 flex justify-between items-center bg-gray-50/50 dark:bg-[#0A0B0F]">
            <div className="flex items-center gap-3">
                <div className="p-2 bg-nobel-gold/10 rounded-lg text-nobel-gold">
                    <QrCode size={20} />
                </div>
                <h3 className="font-serif text-xl text-[#0A0B0F] dark:text-white font-bold">Receive Assets</h3>
            </div>
            <button onClick={onClose} className="p-2 text-stone-400 hover:text-stone-900 dark:hover:text-white transition-colors">
                <X size={20} />
            </button>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto custom-scrollbar max-h-[75vh]">
            {/* Chain Selector */}
            <div className="space-y-3">
                <label className="text-[10px] font-bold text-stone-500 uppercase tracking-[0.2em] ml-1">Network</label>
                <div className="grid grid-cols-2 gap-2">
                   {(['SUI', 'SOL'] as const).map(chain => (
                       <button 
                            key={chain} 
                            onClick={() => setSelectedChain(chain)} 
                            className={`flex items-center justify-center gap-2 py-3 rounded-card font-bold text-xs transition-all border ${
                                selectedChain === chain 
                                    ? 'bg-[#0A0B0F] text-white dark:bg-white dark:text-black border-transparent shadow-lg' 
                                    : 'bg-white dark:bg-white/5 border-stone-200 dark:border-white/10 hover:border-stone-300 dark:hover:border-white/20 text-stone-600 dark:text-stone-400'
                            }`}
                        >
                           <ChainIcon chain={chain} className="w-4 h-4" /> {chain === 'SOL' ? 'Solana' : 'Sui'}
                       </button>
                   ))}
                </div>
            </div>

            {/* Token Selector */}
            <div className="space-y-3">
                <label className="text-[10px] font-bold text-stone-500 uppercase tracking-[0.2em] ml-1">Select Asset</label>
                <div className="relative group">
                    <select 
                        value={selectedToken?.mint || ''}
                        onChange={(e) => setSelectedToken(filteredTokens.find(t => t.mint === e.target.value) || null)}
                        className="w-full appearance-none p-4 bg-stone-50 dark:bg-white/5 border border-stone-200 dark:border-white/10 rounded-card text-sm font-bold outline-none focus:ring-2 focus:ring-nobel-gold/20 focus:border-nobel-gold transition-all text-[#0A0B0F] dark:text-white pr-10"
                    >
                        {filteredTokens.map(token => (
                            <option key={token.mint} value={token.mint}>{token.symbol} — {token.name}</option>
                        ))}
                    </select>
                    <ChevronDown size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none group-hover:text-nobel-gold transition-colors" />
                </div>
            </div>
            
            {/* QR Code Container */}
            <div className="flex flex-col items-center justify-center py-4 bg-stone-50 dark:bg-black/20 rounded-card border border-stone-100 dark:border-white/5">
                <div className="p-4 bg-white rounded-xl shadow-inner border border-stone-200 relative group overflow-hidden">
                   <QRCodeCanvas value={address || ''} size={180} bgColor="#ffffff" fgColor="#0A0B0F" level="H" />
                   <div 
                        onClick={() => address && handleCopy(address)}
                        className="absolute inset-0 bg-white/90 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col items-center justify-center cursor-pointer"
                    >
                       <Copy size={32} className="text-nobel-gold mb-2" />
                       <span className="text-[10px] font-black uppercase tracking-widest text-[#0A0B0F]">Copy Address</span>
                   </div>
                </div>
                <div className="mt-6 px-4 w-full">
                    <div className="text-[10px] font-bold text-stone-400 uppercase tracking-[0.3em] text-center mb-2">Deposit Address</div>
                    <div className="p-3 bg-white dark:bg-[#1A1D23] rounded-xl border border-stone-200 dark:border-white/10 flex items-center justify-between group shadow-sm">
                        <span className="font-mono text-[11px] text-stone-700 dark:text-stone-300 truncate pr-4">{address}</span>
                        <div className="flex gap-1.5">
                            <button 
                                onClick={() => address && handleCopy(address)}
                                className="p-2 text-stone-400 hover:text-nobel-gold transition-colors"
                                title="Copy"
                            >
                                {copied ? <CheckCircle size={16} className="text-green-500" /> : <Copy size={16}/>}
                            </button>
                            <button 
                                onClick={handleShare}
                                className="p-2 text-stone-400 hover:text-blue-500 transition-colors"
                                title="Share"
                            >
                                <Share2 size={16} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Warning Box */}
            <div className="p-4 bg-amber-50 dark:bg-amber-900/10 rounded-card border border-amber-100 dark:border-amber-500/20 flex gap-3">
                <Info size={18} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <p className="text-[11px] text-amber-800 dark:text-stone-400 leading-relaxed">
                    Ensure you are sending <strong className="text-amber-900 dark:text-amber-300">{selectedToken?.symbol}</strong> on the <strong className="text-amber-900 dark:text-amber-300">{selectedChain}</strong> network. GhostPay cannot recover assets sent to the incorrect address or network.
                </p>
            </div>
        </div>

        {/* Footer Actions */}
        <div className="p-4 bg-gray-50 dark:bg-[#0A0B0F] border-t border-stone-100 dark:border-white/5">
            <Button 
                onClick={onClose} 
                variant="secondary" 
                className="w-full h-12 rounded-card border-none bg-[#0A0B0F] dark:bg-white text-white dark:text-black hover:opacity-90 transition-opacity"
            >
                Done
            </Button>
        </div>
      </motion.div>
    </motion.div>
    );
};
